/**
 * Authentication utilities.
 *
 * Primary auth: JWT is stored server-side in an httpOnly cookie (`dashlink_token`).
 * The frontend never touches the token — every axios call MUST send cookies
 * (see `axios.defaults.withCredentials = true` set globally in App entry).
 * This protects against XSS token exfiltration.
 *
 * User object is cached in sessionStorage as a UX optimization so the
 * initial render doesn't need a round-trip; source of truth is /api/users/me.
 *
 * Legacy shims: `getToken` and `saveAuthData` are retained as no-ops so
 * existing call sites that add `Authorization: Bearer` headers keep compiling.
 * Those headers are ignored by the backend when a valid cookie is present.
 */
import axios from 'axios';

const USER_KEY = 'user';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const saveUser = (user) => {
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const getUser = () => {
  const raw = sessionStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
};

export const clearAuthData = () => {
  sessionStorage.removeItem(USER_KEY);
};

export const updateUserData = (updates) => {
  const currentUser = getUser();
  if (currentUser) {
    const updated = { ...currentUser, ...updates };
    saveUser(updated);
    return updated;
  }
  return null;
};

// Best-effort: verify session against server. Returns user or null.
export const fetchCurrentUser = async () => {
  try {
    const { data } = await axios.get(`${API}/users/me`);
    saveUser(data);
    return data;
  } catch (e) {
    clearAuthData();
    return null;
  }
};

// Logout hits the backend to clear the cookie, then wipes local user cache.
export const logout = async () => {
  try {
    await axios.post(`${API}/auth/logout`, {});
  } catch (e) {
    // Ignore network errors — we still clear local state
  }
  clearAuthData();
};

// ============ Legacy Compat Shims (used by existing call sites) ============
// getToken returns null now — cookie handles auth. Bearer header is ignored
// server-side when a valid cookie is present.
export const getToken = () => null;

export const saveAuthData = (_token, user) => {
  // Token is ignored — server set it as an httpOnly cookie. Cache user only.
  saveUser(user);
};

export const isAuthenticated = () => !!getUser();
